import { PUBLIC_PATH } from '@genesislcap/foundation-utils';

/**
 * Reloads the page once if the deployed `index.html` references different
 * script bundles than the ones currently running. Covers users with long-lived
 * Chrome sessions, SSO return, and logout/login paths that load a stale shell.
 * Real cache policy lives in nginx; this is a safety net.
 */
const RELOADED_FOR_KEY = 'munis-portal-version-reload-for';
const RELOADED_FROM_KEY = 'munis-portal-version-reload-from';

export function extractScriptFingerprint(html: string): string {
  return [...html.matchAll(/\ssrc=["']([^"']+\.(?:js|mjs))["']/gi)]
    .map((m) => m[1].trim())
    .sort()
    .join('|');
}

/** App shell path used for version probes (not the current SPA route). */
export function getAppShellPath(): string {
  const publicPath = typeof PUBLIC_PATH !== 'undefined' ? PUBLIC_PATH : '';
  if (!publicPath || publicPath === '/') {
    return '/';
  }
  return publicPath.endsWith('/') ? publicPath : `${publicPath}/`;
}

export function getAppShellFetchUrl(cacheBust: number = Date.now()): string {
  const shell = getAppShellPath();
  return `${shell}?_=${cacheBust}`;
}

export function shouldReloadForVersion(
  current: string,
  deployed: string,
  lastAttempt: string | null,
  lastFrom: string | null,
): boolean {
  if (!deployed || deployed === current) {
    return false;
  }
  // Already reloaded trying to reach `deployed` from `current` and it did not help.
  if (lastAttempt === deployed && lastFrom === current) {
    return false;
  }
  return true;
}

function currentScriptFingerprint(): string {
  return Array.from(document.querySelectorAll('script[src]'))
    .map((s) => s.getAttribute('src') ?? '')
    .filter(Boolean)
    .sort()
    .join('|');
}

function reloadForLatestVersion(): void {
  location.replace(getAppShellFetchUrl(Date.now()));
}

async function fetchDeployedFingerprint(): Promise<string> {
  const res = await fetch(getAppShellFetchUrl(Date.now()), {
    cache: 'reload',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });
  if (!res.ok) {
    return '';
  }
  return extractScriptFingerprint(await res.text());
}

export async function ensureLatestAppVersion(): Promise<void> {
  try {
    const current = currentScriptFingerprint();
    const deployed = await fetchDeployedFingerprint();
    const lastAttempt = sessionStorage.getItem(RELOADED_FOR_KEY);
    const lastFrom = sessionStorage.getItem(RELOADED_FROM_KEY);

    if (!shouldReloadForVersion(current, deployed, lastAttempt, lastFrom)) {
      return;
    }

    sessionStorage.setItem(RELOADED_FOR_KEY, deployed);
    sessionStorage.setItem(RELOADED_FROM_KEY, current);
    reloadForLatestVersion();
  } catch {
    // Network/parse failure — leave user on current build.
  }
}
